// auth.interceptor.ts
import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private authService = inject(AuthService);

    private isRefreshing = false;
    // Subject to coordinate the refresh flow across concurrent 401s

    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const accessToken = this.authService.getAccessToken();

        let authReq = req;
        if (accessToken) {
            authReq = this.addTokenHeader(req, accessToken);
        }

        return next.handle(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
                // If it's a 401 and not the refresh token request itself, attempt to handle
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    // Check if the request is for the refresh endpoint (assuming your AuthService handles this check internally)
                    // If your AuthService uses the interceptor for its refresh call, you MUST exclude it here to prevent a loop.
                    if (this.isRefreshRequest(authReq)) {
                        // If the refresh request itself returns 401, then we logout immediately
                        this.authService.logout();
                        return throwError(() => error);
                    }

                    // Attempt to handle the expired access token
                    return this.handle401Error(authReq, next);
                }
                return throwError(() => error);
            })
        );
    }

    private isRefreshRequest(request: HttpRequest<unknown>): boolean {
        // IMPORTANT: Adjust this to match your actual refresh token endpoint
        // Example assumes the refresh endpoint contains 'auth/refresh-token'
        return request.url.includes('/auth/refresh-token');
    }

    private addTokenHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
        return request.clone({
            withCredentials: true,
            setHeaders: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null); // Signal that token is currently unavailable

            // Step 1: Attempt to refresh the token
            return this.authService.refreshAccessToken().pipe(
                switchMap((response: { token: string }) => {
                    this.isRefreshing = false;
                    const newAccessToken = response.token;
                    this.authService.setAccessToken(newAccessToken);

                    // Step 2: Inform all waiting requests that the token is ready
                    this.refreshTokenSubject.next(newAccessToken);

                    // Step 3: Retry the initial failed request with the new token
                    return next.handle(this.addTokenHeader(request, newAccessToken));
                }),
                catchError(err => {
                    // This catches failure of the refreshAccessToken call
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(null);
                    // Crucial: The refresh failed, so we log out (and navigate to login screen)
                    this.authService.forceSignOut();
                    return throwError(() => err);
                })
            );
        }

        // If a refresh is already in progress, queue the current request
        return this.refreshTokenSubject.pipe(
            filter(token => token != null), // Wait until a non-null token is emitted
            take(1), // Take the first emitted token and complete
            switchMap(token => {
                // Retry the initial failed request with the new token
                return next.handle(this.addTokenHeader(request, token));
            })
        );
    }
}
